import { useState, useEffect } from "react";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { Navigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Assets
import SignInImg from "assets/images/065759206512449.66ce05c7449fc.jpg";
import "./sign-in.scss";

function Basic() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorText, setErrorText] = useState("");
  const [goHome, setGoHome] = useState(false);
  const [loadingReq, setLoadingReq] = useState(false);

  const auth = getAuth();
  const [user] = useAuthState(auth);

  useEffect(() => {
    if (user) setGoHome(true);
  }, [user]);

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const handleSignIn = async (e) => {
    if (e) e.preventDefault();
    setErrorText("");

    if (!validateEmail(email)) {
      setErrorText("Please enter a valid email address.");
      return;
    }
    if (password.length < 6) {
      setErrorText("Password must be at least 6 characters.");
      return;
    }

    setLoadingReq(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setGoHome(true);
    } catch (error) {
      setErrorText("Invalid credentials. Please try again.");
    } finally {
      setLoadingReq(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!validateEmail(email)) {
      setErrorText("Please enter your email above first.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setErrorText("Reset link sent! Please check your inbox.");
    } catch (error) {
      setErrorText(error.message);
    }
  };

  if (goHome) return <Navigate to="/dashboard" />;

  return (
    <BasicLayout>
      <div className="full-page-signin">
        <Grid container className="signin-container">
          {/* Left Side: Visual/Branding */}
          <Grid
            item
            xs={12}
            md={6}
            lg={7}
            className="login-visual-side"
            style={{ backgroundImage: `url(${SignInImg})` }}
          >
            <div className="visual-overlay" />

            <div className="visual-content-bottom">
              <MDTypography variant="h2" color="white" fontWeight="bold">
                Welcome Back
              </MDTypography>
              <MDTypography
                variant="h6"
                color="white"
                opacity={0.8}
                fontWeight="light"
              >
                Med-CO is a leading provider of medical software and digital
                healthcare solutions{" "}
              </MDTypography>
            </div>
          </Grid>

          {/* Right Side: Form */}
          <Grid item xs={12} md={6} lg={5} className="login-form-side">
            <MDBox className="form-content-wrapper">
              <MDBox mb={4} textAlign="center">
                <MDTypography variant="h3" fontWeight="bold">
                  Sign In
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Enter your credentials to access your account
                </MDTypography>
              </MDBox>

              <MDBox component="form" role="form" onSubmit={handleSignIn}>
                <MDBox mb={3}>
                  <MDInput
                    type="email"
                    label="Email"
                    fullWidth
                    variant="standard"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </MDBox>
                <MDBox mb={3}>
                  <MDInput
                    type="password"
                    label="Password"
                    fullWidth
                    variant="standard"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </MDBox>

                {errorText && (
                  <MDBox
                    mb={2}
                    className={`status-message-box ${errorText.includes("sent") ? "success" : "error"}`}
                  >
                    <MDTypography variant="caption" color="inherit">
                      {errorText}
                    </MDTypography>
                  </MDBox>
                )}

                <MDBox mt={4} mb={1}>
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="info"
                    fullWidth
                    size="large"
                    onClick={handleSignIn}
                    disabled={loadingReq}
                  >
                    {loadingReq ? "Signing in..." : "Sign In"}
                  </MDButton>
                </MDBox>

                <MDBox mt={3} textAlign="center">
                  <MDTypography
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                    sx={{ cursor: "pointer", fontSize: "0.85rem" }}
                    onClick={handleForgotPassword}
                  >
                    Forgot Password?
                  </MDTypography>
                </MDBox>
              </MDBox>

              {/* Powered By Footer */}
              <MDBox mt={2} textAlign="center">
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  opacity={0.6}
                >
                  Powered by{" "}
                  <MDTypography variant="button" color="info" fontWeight="bold">
                    TheDevHouse
                  </MDTypography>
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </div>
    </BasicLayout>
  );
}
export default Basic;
